// Conversion tracking fan-out for the Client Portal.
//
// Every milestone in the portal calls trackEvent() here; this module is the single place that
// decides what leaves the browser. The portal handles PHI, so properties pass through a strict
// ALLOWED_KEYS allowlist — anything not named below is dropped, whatever the caller passes.
//
// Never sent: patient name, email, phone, date of birth, address, insurance details, diagnosis,
// reason/symptom free text, prescriptions, lab or document data, and card/billing details.

import { captureEvent } from "./analytics";
import { pushToDataLayer } from "./gtm";
import { getVisitorId } from "./visitorId";
import { getAttribution, MARKETING_PARAMS } from "./attribution";
import { sanitizeLocation, sanitizePath } from "./sanitize";
import { trackEvents } from "./events";
import { getAuthProfile, getSearchParams } from "../utils/validation";

// Re-exported so existing importers keep reaching them through this module.
export { sanitizeLocation, sanitizePath };

const ALLOWED_KEYS = [
    "visitor_id",
    "user_id",
    "member_id",
    "account_code",
    "entry_type",
    "referrer_host",
    "page_path",
    "page_location",
    "service_code",
    "speciality_code",
    "step_code",
    "step_index",
    "method_id",
    "encounter_mode_id",
    "payment_method",
    "transaction_id",
    "payment_id",
    "value",
    "currency",
    ...MARKETING_PARAMS,
];

// Keys that describe one milestone rather than the visit. The data layer is a single merged model,
// so a key pushed once stays readable by every later event: dc_payment_complete would report the
// step_code of the last wizard step, and a dc_page_view after a booking would report that booking's
// transaction_id. Every push therefore clears the scoped keys it does not set itself. The visit-wide
// keys — visitor_id, user_id, account_code, the page address and the attribution set — are left to
// persist, which is what makes them available to events that never set them.
const SCOPED_KEYS = [
    "service_code",
    "speciality_code",
    "step_code",
    "step_index",
    "method_id",
    "encounter_mode_id",
    "payment_method",
    "transaction_id",
    "payment_id",
    "value",
    "currency",
];

const MAX_VALUE_LENGTH = 100;
// The URL keys carry a whole sanitized address, which the general cap would cut mid-path and hand
// GA4 a broken page_location. They are built from an allowlist, so there is no free text to bound.
const URL_KEYS = ["page_path", "page_location"];
const MAX_URL_LENGTH = 500;
const REPORTED_KEY = "dc_gtm_reported";
const REPORTED_LIMIT = 50;
const INTAKE_STARTED_KEY = "dc_intake_started";
const LAST_STEP_KEY = "dc_last_step";
const LANDING_SENT_KEY = "dc_landing_sent";

let _accountCode;

/** Records the account code so every event can carry it without the call sites passing it. */
export const setTrackingAccount = (accountCode) => {
    _accountCode = accountCode;
};

const sanitizeValue = (value, key) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
        const trimmed = value.trim();
        const limit = URL_KEYS.includes(key) ? MAX_URL_LENGTH : MAX_VALUE_LENGTH;
        return trimmed ? trimmed.slice(0, limit) : undefined;
    }
    return undefined;
};

/** Drops every property that is not on the allowlist and normalises the ones that survive. */
export const sanitizeProps = (props = {}) => {
    const safe = {};
    if (!props || typeof props !== "object") return safe;

    ALLOWED_KEYS.forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(props, key)) return;
        const value = sanitizeValue(props[key], key);
        if (value !== undefined) safe[key] = value;
    });

    return safe;
};

const baseProps = () => {
    const props = { visitor_id: getVisitorId() };
    if (_accountCode) props.account_code = _accountCode;

    try {
        if (getAuthProfile.getProfileAuth()) {
            const userId = localStorage.getItem("memberId");
            if (userId) {
                props.user_id = userId;
                props.member_id = userId;
            }
        }
    } catch (e) {
        // Storage unavailable — the event still goes out without a user id.
    }

    return props;
};

/**
 * Retracts every scoped key this event does not carry, so the data layer holds only what the event
 * actually reported. Only the data layer needs this — PostHog receives each event's properties on
 * their own.
 *
 * undefined, not null. Both stop the previous value being read, but a container variable resolving
 * to null still hands GA4 the parameter with an empty value, so the event arrives carrying a dozen
 * blank columns; undefined leaves the variable unset and the parameter is omitted outright. This is
 * the opposite of resetGtmUser, which clears GA4's built-in user_id field through a gtag `set` and
 * does need null. Verified on the wire against the published container, 2026-08-19.
 */
const withScopeReset = (payload) => {
    const cleared = {};
    SCOPED_KEYS.forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(payload, key)) cleared[key] = undefined;
    });

    return { ...cleared, ...payload };
};

/** Sanitizes once, then fans the milestone out to PostHog and the GTM data layer. */
export const trackEvent = (eventName, props = {}) => {
    if (!eventName) return;

    const payload = sanitizeProps({ ...baseProps(), ...props });

    captureEvent(eventName, payload);
    pushToDataLayer({ event: eventName, ...withScopeReset(payload) });
};

/**
 * Fires a page view for a react-router location. page_location is the sanitized absolute URL the
 * GA4 tag should report in place of the raw address bar it would otherwise collect itself.
 */
export const trackPageView = (location) => {
    if (!location) return;
    trackEvent(trackEvents.pageView, {
        page_path: sanitizePath(location.pathname, location.search),
        page_location: sanitizeLocation(location.pathname, location.search),
    });
};

/**
 * Reports a booking wizard step. The first ordered step seen in a tab session also raises the
 * "intake started" milestone, so the caller does not have to know which step comes first — that
 * order is configured per account.
 *
 * The flow is also driven with control codes that hold no position in the wizard (INSURANCE,
 * PAYMENTINITIATE, PAYMENTUPDATE, PAYMENTFAILED), which reach here with an index of -1. They are
 * still worth a step event, but they report no step_index and must not open the funnel — a patient
 * coming back from the payment gateway would otherwise "start" the intake at the payment step.
 *
 * handleServiceFlow re-enters the same step on its own — a step that resolves its data calls back
 * with the code it is already on — so the same code arrives twice in a row and that stage gets
 * counted twice. A repeat with no other step in between is not movement through the funnel and is
 * dropped; a genuine return to an earlier step (STR then PLR then STR) still reports, because
 * another step separates the two.
 */
export const trackIntakeStep = (stepCode, stepIndex) => {
    if (!stepCode) return;

    try {
        if (sessionStorage.getItem(LAST_STEP_KEY) === stepCode) return;
        sessionStorage.setItem(LAST_STEP_KEY, stepCode);
    } catch (e) {
        // Storage unavailable — report anyway rather than lose the step.
    }

    const isOrderedStep = Number.isInteger(stepIndex) && stepIndex >= 0;

    // Which service the patient is booking, so the funnel can be read per service rather than as one
    // undifferentiated drop-off curve. The wizard carries it in the address for the whole flow, and
    // reading it here keeps every step consistent with dc_consultation_submit without each call site
    // having to remember to pass it.
    const serviceCode = getSearchParams("service");

    try {
        if (isOrderedStep && !sessionStorage.getItem(INTAKE_STARTED_KEY)) {
            sessionStorage.setItem(INTAKE_STARTED_KEY, "1");
            trackEvent(trackEvents.intakeStart, { step_code: stepCode, service_code: serviceCode });
        }
    } catch (e) {
        // Storage unavailable — fall through and still report the step itself.
    }

    const props = { step_code: stepCode };
    if (isOrderedStep) props.step_index = stepIndex;
    if (serviceCode) props.service_code = serviceCode;

    trackEvent(trackEvents.intakeStep, props);
};

/**
 * Drops the per-booking guards so a second booking in the same tab reports its own funnel entry.
 * Both markers live for the life of the tab, so without this the next booking silently raises no
 * dc_intake_start and can lose its first step to the consecutive-step check. Called where the flow
 * session itself is cleared, on the confirmation page — the only point at which a booking is
 * unambiguously finished. An abandoned booking has no such signal and keeps its guards.
 */
export const resetIntakeSession = () => {
    try {
        sessionStorage.removeItem(INTAKE_STARTED_KEY);
        sessionStorage.removeItem(LAST_STEP_KEY);
    } catch (e) {
        // Storage unavailable — the guards were never written either.
    }
};

// Milestones that map to a single real-world occurrence — a booking, a completed consultation —
// must not be re-reported when the patient reloads the page or revisits the list they came from.
const readReported = () => {
    try {
        const stored = localStorage.getItem(REPORTED_KEY);
        const parsed = stored ? JSON.parse(stored) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
};

const markReported = (keys) => {
    try {
        const merged = [...readReported(), ...keys].slice(-REPORTED_LIMIT);
        localStorage.setItem(REPORTED_KEY, JSON.stringify(merged));
    } catch (e) {
        // Storage unavailable — the milestone may repeat, which is preferable to losing it.
    }
};

/** Fires a milestone at most once for a given business key. */
export const trackOnce = (eventName, key, props = {}) => {
    if (!eventName || !key) return;

    const reportedKey = `${eventName}:${key}`;
    if (readReported().includes(reportedKey)) return;

    trackEvent(eventName, props);
    markReported([reportedKey]);
};

/**
 * Raises the "patient landed on the Client Portal" milestone with how they arrived. Fires once per
 * tab session: sign-in, sign-out and the SSO entry handler all navigate with a full page load, and
 * without this guard a single visit reports several landings carrying identical attribution.
 */
export const trackPortalLanding = () => {
    try {
        if (sessionStorage.getItem(LANDING_SENT_KEY)) return;
        sessionStorage.setItem(LANDING_SENT_KEY, "1");
    } catch (e) {
        // Storage unavailable — report anyway rather than lose the milestone.
    }

    trackEvent(trackEvents.portalLanding, getAttribution());
};
